import { ProviderData } from "@/types/provider";
import {
  Wallet,
  Clock,
  TrendingUp,
  CheckCircle2,
  BadgeCheck,
  Shield,
  CreditCard,
  Percent,
  Building2,
  Users
} from "lucide-react";
import anyfinLogo from "@/assets/anyfin_stor.webp";
import ucLogo from "@/assets/UC.png";
import bankTransferIcon from "@/assets/banköverföring.png";
import trustlyLogo from "@/assets/Trustly.png";
import swishLogo from "@/assets/swish_stor.svg";

export const anyfinData: ProviderData = {
  id: "anyfin",
  name: "Anyfin",
  slug: "anyfin",
  logo: anyfinLogo,
  type: "refinance",
  trustpilot: {
    rating: 4.6,
    reviews: 3461,
    url: "https://se.trustpilot.com/review/anyfin.com"
  },
  hero: {
    badges: [
      { text: "Omfinansiera dina skulder - bättre villkor", variant: "default" }
    ],
    description: "Anyfin hjälper dig att refinansiera dina befintliga lån och krediter till en lägre ränta. Helt kostnadsfritt och enkelt.",
    ctaText: "Ansök hos Anyfin",
    ctaButtonText: "Ansök hos Anyfin",
    ctaButtonVariant: "default",
    stickyCTASubtitle: "Omfinansiera dina skulder"
  },
  stats: [
    {
      icon: Percent,
      value: "Från 3,95%",
      label: "Effektiv ränta",
      description: "Individuell prissättning"
    },
    {
      icon: Wallet,
      value: "300 000 kr",
      label: "Maxbelopp",
      description: "500 000 kr med medlåntagare"
    },
    {
      icon: Clock,
      value: "1-15 år",
      label: "Återbetalningstid",
      description: "Flexibel löptid"
    },
    {
      icon: CheckCircle2,
      value: "Max 1",
      label: "Betalningsanmärkning",
      description: "Godkänns vid bra betalningsförmåga"
    },
    {
      icon: Shield,
      value: "UC",
      label: "Kreditupplysning",
      description: "Påverkar din UC-Score"
    },
    {
      icon: CreditCard,
      value: "Direkt",
      label: "Utbetalning",
      description: "Till dina nuvarande långivare"
    }
  ],
  termsAndConditions: [
    { term: "Lånebelopp", value: "5 000 – 300 000 kr" },
    { term: "Med medlåntagare", value: "Upp till 500 000 kr" },
    { term: "Nominell ränta", value: "Individuell, från 3,95%" },
    { term: "Effektiv ränta", value: "Individuell, från 3,95%" },
    { term: "Löptid", value: "1 – 15 år" },
    { term: "Uppläggningsavgift", value: "0 kr" },
    { term: "Fakturaavgift", value: "0 kr" },
    { term: "Aviavgift", value: "0 kr" },
    { term: "Lån utan UC", value: "Nej" },
    { term: "Svar på ansökan", value: "Samma dag" }
  ],
  loanRequirements: [
    { requirement: "Minsta ålder", value: "18 år" },
    { requirement: "Legitimering", value: "Ja" },
    { requirement: "Folkbokförd i Sverige", value: "Ja" },
    { requirement: "Svenskt personnummer", value: "Ja" },
    { requirement: "Svenskt mobilnummer", value: "Ja" },
    { requirement: "Svenskt kontonummer", value: "Ja" },
    { requirement: "Fast inkomst", value: "Ja" },
    { requirement: "Betalningsanmärkning OK?", value: "Max 1" }
  ],
  features: {
    pros: [
      "Inga avgifter - helt kostnadsfritt",
      "Enkel digital process",
      "Refinansiera flera lån samtidigt",
      "Betalar av dina gamla lån direkt"
    ],
    cons: [
      "Endast för refinansiering av befintliga krediter"
    ]
  },
  interestRateSection: {
    title: "Anyfins ränta",
    description: "Att ansöka om refinansiering via Anyfin är kostnadsfritt. När du gjort en ansökan via Anyfin bedömer de din kreditvärdighet utifrån den kreditupplysning som görs hos UC, och lånevillkoren blir sedan individuellt anpassade. De tittar till exempel på din betalningsförmåga och kreditrisk.",
    rateTable: [
      { label: "Lånebelopp (lägsta – högsta)", value: "5 000 – 300 000 kr" },
      { label: "Nominell ränta", value: "3,55% – " },
      { label: "Effektiv ränta", value: "3,55% – " },
      { label: "Uppläggningsavgift", value: "0" },
      { label: "Aviavgift", value: "0" },
      { label: "Förtidsinlösen", value: "Kostnadsfri" }
    ]
  },
  payoutSection: {
    title: "Anyfins utbetalning",
    description: "När du ansöker om refinansiering via Anyfin kan du räkna med att det tar cirka 1-2 arbetsdagar för pengarna att komma in på ditt konto.",
    payoutTable: [
      { timeframe: "Beviljad före 12:00", duration: "1-2 dagar" },
      { timeframe: "Beviljad efter 12:00", duration: "1-2 dagar" }
    ],
    disclaimer: "Observera: Utbetalningar sker endast under bankdagar. Om du ansöker eller får godkännande på en helg eller helgdag kan det dröja längre innan pengarna finns tillgängliga på ditt konto."
  },
  aboutSections: [
    {
      title: "Om Anyfin",
      content: [
        "Anyfin är en fintechaktör som specialiserar sig på refinansiering av privatlån och krediter. Deras tjänst hjälper dig att samla och refinansiera dina befintliga lån till en lägre ränta.",
        "Genom att använda smart teknologi kan Anyfin erbjuda en enkel och kostnadseffektiv lösning för att minska dina månadskostnader och totala ränteutgifter."
      ]
    },
    {
      title: "Hur fungerar Anyfin?",
      content: [
        "Processen är enkel: Du ansöker digitalt och anger vilka lån du vill refinansiera. Anyfin gör sedan en kreditupplysning och bedömer dina möjligheter.",
        "Om du godkänns betalar Anyfin av dina gamla lån direkt till långivarna, och du får ett nytt lån med bättre villkor hos Anyfin. Du slipper själv hantera överföringen av pengar mellan banker."
      ]
    }
  ],
  faq: [
    {
      question: "Vad kostar det att använda Anyfin?",
      answer: "Det är helt kostnadsfritt att ansöka om refinansiering via Anyfin. Det tillkommer inga uppläggnings-, avi- eller fakturaavgifter."
    },
    {
      question: "Vilka lån kan jag refinansiera via Anyfin?",
      answer: "Du kan refinansiera de flesta typer av privatlån och kreditkortsskulder. Anyfin betalar av dina gamla lån direkt till långivarna."
    },
    {
      question: "Hur snabbt får jag pengarna?",
      answer: "När din ansökan är godkänd tar det vanligtvis 1-2 bankdagar innan pengarna betalas ut till dina nuvarande långivare."
    },
    {
      question: "Kan jag refinansiera om jag har betalningsanmärkningar?",
      answer: "Anyfin kan i vissa fall godkänna refinansiering om du har max 1 betalningsanmärkning, förutsatt att du har god betalningsförmåga idag."
    },
    {
      question: "Påverkas min UC-Score?",
      answer: "Ja, Anyfin gör en kreditupplysning hos UC när du ansöker, vilket kan påverka din UC-Score."
    }
  ],
  customerService: {
    description: "Anyfins kundtjänst finns tillgänglig för att hjälpa dig med frågor om refinansiering och din ansökan.",
    phone: "010-750 31 00",
    phoneHours: ["Måndag-Fredag: 09:00-17:00"],
    email: "hej@anyfin.com",
    emailHours: ["Svarar inom 24 timmar"]
  },
  companyInfo: {
    name: "Anyfin AB",
    orgNumber: "559093-5163",
    address: "Drottninggatan 71",
    city: "111 36 Stockholm"
  },
  affiliateLink: {
    linkId: "anyfin-affiliate-link-id",
    trackingSource: "anyfin_page",
    url: "https://go.adt231.net/t/t?a=1764858223&as=2024680921&t=2&tk=1"
  },
  seo: {
    title: "Anyfin Refinansiering 2025 - Sänk Dina Lånekostnader | Från 3,95%",
    description: "Refinansiera dina lån med Anyfin och spara pengar. Helt kostnadsfritt, ingen uppläggningsavgift. Låna 5 000-300 000 kr med effektiv ränta från 3,95%. Ansök idag!",
    keywords: [
      "anyfin",
      "refinansiering",
      "samla lån",
      "sänk räntan",
      "privatlån",
      "kreditkortsskuld",
      "låg ränta"
    ]
  },
  schema: {
    productName: "Anyfin Refinansiering",
    description: "Refinansiera dina befintliga lån och krediter till lägre ränta. Helt kostnadsfritt med direkt utbetalning till dina långivare.",
    url: "https://klarfinans.se/sv-SE/privatlan/anyfin",
    provider: "Anyfin",
    interestRate: {
      min: 3.95,
      max: null
    },
    minAmount: 5000,
    maxAmount: 300000,
    loanTerm: ["1 år", "2 år", "3 år", "5 år", "10 år", "15 år"],
    featureList: [
      "Refinansiera befintliga lån och krediter",
      "Helt kostnadsfritt - inga avgifter",
      "Effektiv ränta från 3,95%",
      "Lånebelopp 5 000 - 300 000 kr",
      "Upp till 500 000 kr med medlåntagare",
      "Flexibel återbetalningstid 1-15 år",
      "Direkt utbetalning till dina långivare",
      "Enkel digital process"
    ]
  }
};
